/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import apiService from "@/services/apiService";
import { getClosestPoliceStation } from "@/lib/helper";
import useErrorStore from "@/store/confirmationStore";
import { validateAge, validateDate, validateDateRange, validateNumber, validateRequired } from "@/lib/validationHelper";
import { showValidationToast } from "@/lib/toastHelper";
import { getNestedValue, setNestedError } from "@/lib/utils";
import { getUserInfo } from "@/services/userService";

export interface Respondent {
  name: string;
  father_name: string;
  age: string;
  address?: string;
}

export interface CourtAppearance {
  date: string;
  time: string;
  location: string;
}

export interface BoundDownOrderCreate {
  meaning: string;
  dateOfOrder: string;
  duration: string;
  bondAmount: number;
  amountPerSurety: number;
  totalSureties: number;
  suretyConditions: string;
  consequenceOnDefault: string;
  summary: string;
  effectiveFrom: string;
  effectiveTo: string;
  createdBy?: { connect: { id: string } };
}

export interface Bnsl129NoticeForm {
  id?: string;
  description: string;
  caseNumber: string;
  section: string;
  policeStation: string;
  district: string;
  court: string;
  petitioner: string;
  respondent: Respondent[];
  noticeSubject: string;
  reportDate: string;
  noticeIssueDate: string;
  courtAppearance: CourtAppearance;
  legalAction: string;
  magistrateSignature: string;
  boundDownOrder?: BoundDownOrderCreate;
  createdBy?: { connect: { id: string } };
  boundDownOrderId?: string;
  parentDocumentId?: string;
  file_name?: string;
  documentType?: string;
  document?: { connect: { id: string } };
}

interface ValidationErrors {
  [key: string]: string | ValidationErrors | ValidationErrors[];
}

interface Bnsl129NoticePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data?: Partial<Bnsl129NoticeForm>;
}

export function Bnsl129NoticePopup({ open, onOpenChange, data }: Bnsl129NoticePopupProps) {
  const showError = useErrorStore((s) => s.showError);
  const [formData, setFormData] = useState<Partial<Bnsl129NoticeForm>>({
    respondent: [{ name: "", father_name: "", age: "", address: "" }],
    courtAppearance: { date: "", time: "", location: "" },
    boundDownOrder: {
      meaning: "",
      dateOfOrder: "",
      duration: "",
      bondAmount: 0,
      amountPerSurety: 0,
      totalSureties: 0,
      suretyConditions: "",
      consequenceOnDefault: "",
      summary: "",
      effectiveFrom: "",
      effectiveTo: "",
    },
  });
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    if (data) {
      setFormData({
        respondent: [{ name: "", father_name: "", age: "", address: "", ...data.respondent }],
        courtAppearance: { date: "", time: "", location: "", ...data.courtAppearance },
        boundDownOrder: {
          meaning: data.boundDownOrder?.meaning ?? "",
          dateOfOrder: data.boundDownOrder?.dateOfOrder ?? "",
          duration: data.boundDownOrder?.duration ?? "",
          bondAmount: data.boundDownOrder?.bondAmount ?? 0,
          amountPerSurety: data.boundDownOrder?.amountPerSurety ?? 0,
          totalSureties: data.boundDownOrder?.totalSureties ?? 0,
          suretyConditions: data.boundDownOrder?.suretyConditions ?? "",
          consequenceOnDefault: data.boundDownOrder?.consequenceOnDefault ?? "",
          summary: data.boundDownOrder?.summary ?? "",
          effectiveFrom: data.boundDownOrder?.effectiveFrom ?? "",
          effectiveTo: data.boundDownOrder?.effectiveTo ?? "",
        },
        ...data,
      });
    }
  }, [data]);

  

 

  const validateField = (field: string, value: any, context?: any): string => {
    switch (field) {
      case "caseNumber":
        return validateRequired(value, "Case Number");
      case "section":
        return validateRequired(value, "Section");
      case "policeStation":
        return validateRequired(value, "Police Station");
      case "district":
        return validateRequired(value, "District");
      case "court":
        return validateRequired(value, "Court");
      case "petitioner":
        return validateRequired(value, "Petitioner");
      case "noticeSubject":
        return validateRequired(value, "Notice Subject");
      case "legalAction":
        return validateRequired(value, "Legal Action");
      case "magistrateSignature":
        return validateRequired(value, "Magistrate Signature");
      case "reportDate":
        return validateDate(value, "Report Sent Date");
      case "noticeIssueDate":
        // Optional: court notice for 135(3) is usually never received
        return value ? validateDate(value, "Court Notice Date") : "";
      case "description":
        return validateRequired(value, "Description");

      // Respondent validations
      case "respondent.name":
        return validateRequired(value, "Respondent Name");
      case "respondent.father_name":
        return validateRequired(value, "Father's Name");
      case "respondent.age":
        return validateAge(value);

      // Court appearance validations
      case "courtAppearance.date":
        return validateDate(value, "Court Date");
      case "courtAppearance.time":
        return value ? "" : "Court time is required";
      case "courtAppearance.location":
        return validateRequired(value, "Court Location");

      // Bound down order validations
      case "boundDownOrder.meaning":
        return validateRequired(value, "Meaning");
      case "boundDownOrder.dateOfOrder":
        return validateDate(value, "Date of Order");
      case "boundDownOrder.duration":
        return validateRequired(value, "Duration");
      case "boundDownOrder.bondAmount":
        return validateNumber(value, "Bond Amount", 0);
      case "boundDownOrder.amountPerSurety":
        return validateNumber(value, "Amount Per Surety", 0);
      case "boundDownOrder.totalSureties":
        return validateNumber(value, "Total Sureties", 1);
      case "boundDownOrder.suretyConditions":
        return validateRequired(value, "Surety Conditions");
      case "boundDownOrder.consequenceOnDefault":
        return validateRequired(value, "Consequence on Default");
      case "boundDownOrder.summary":
        return validateRequired(value, "Summary");
      case "boundDownOrder.effectiveFrom":
        return validateDate(value, "Effective From Date");
      case "boundDownOrder.effectiveTo": {
        const fromDate = context?.effectiveFrom || formData.boundDownOrder?.effectiveFrom;
        const dateError = validateDate(value, "Effective To Date");
        if (dateError) return dateError;
        return validateDateRange(fromDate, value);
      }

      default:
        return "";
    }
  };

  const setFieldError = (fieldPath: string, error: string) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      const keys = fieldPath.split(".");

      if (keys.length === 1) {
        if (error) {
          newErrors[keys[0]] = error;
        } else {
          delete newErrors[keys[0]];
        }
      } else if (keys.length === 2) {
        if (!newErrors[keys[0]]) newErrors[keys[0]] = {};
        if (error) {
          (newErrors[keys[0]] as any)[keys[1]] = error;
        } else {
          delete (newErrors[keys[0]] as any)[keys[1]];
          if (Object.keys(newErrors[keys[0]] as any).length === 0) {
            delete newErrors[keys[0]];
          }
        }
      } else if (keys.length === 3 && keys[0] === "respondent") {
        const index = parseInt(keys[1]);
        if (!Array.isArray(newErrors.respondent)) {
          newErrors.respondent = [];
        }
        if (!newErrors.respondent[index]) {
          newErrors.respondent[index] = {};
        }
        if (error) {
          (newErrors.respondent[index] as any)[keys[2]] = error;
        } else {
          delete (newErrors.respondent[index] as any)[keys[2]];
          if (Object.keys(newErrors.respondent[index] as any).length === 0) {
            delete newErrors.respondent[index];
          }
        }
      }

      return newErrors;
    });
  };

  const getFieldError = (fieldPath: string): string => {
    const keys = fieldPath.split(".");
    let current: any = validationErrors;

    for (const key of keys) {
      if (current && typeof current === "object") {
        current = current[key];
      } else {
        return "";
      }
    }

    return typeof current === "string" ? current : "";
  };

  const handleFieldBlur = (fieldPath: string, value: any, context?: any) => {
    const error = validateField(fieldPath, value, context);
    setFieldError(fieldPath, error);
  };

  const handleInputChange = (field: keyof Bnsl129NoticeForm, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent: "respondent" | "courtAppearance", key: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [key]: value },
    }));
  };

  const handleNestedArrayChange = (arrayKey: string, index: number, field: string, value: string) => {
    setFormData((prev: any) => {
      const updatedArray = [...(prev[arrayKey] || [])];
      updatedArray[index] = {
        ...updatedArray[index],
        [field]: value,
      };
      return {
        ...prev,
        [arrayKey]: updatedArray,
      };
    });
  };

  const handleBoundDownOrderChange = (key: keyof BoundDownOrderCreate, value: any) => {
    setFormData((prev) => ({
      ...prev,
      boundDownOrder: {
        meaning: prev.boundDownOrder?.meaning ?? "",
        dateOfOrder: prev.boundDownOrder?.dateOfOrder ?? "",
        duration: prev.boundDownOrder?.duration ?? "",
        bondAmount: prev.boundDownOrder?.bondAmount ?? 0,
        amountPerSurety: prev.boundDownOrder?.amountPerSurety ?? 0,
        totalSureties: prev.boundDownOrder?.totalSureties ?? 0,
        suretyConditions: prev.boundDownOrder?.suretyConditions ?? "",
        consequenceOnDefault: prev.boundDownOrder?.consequenceOnDefault ?? "",
        summary: prev.boundDownOrder?.summary ?? "",
        effectiveFrom: prev.boundDownOrder?.effectiveFrom ?? "",
        effectiveTo: prev.boundDownOrder?.effectiveTo ?? "",
        [key]: value,
      },
    }));
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    let hasErrors = false;

    // Validate all required fields
    const requiredFields = ["caseNumber", "section", "policeStation", "district", "court", "petitioner", "noticeSubject", "legalAction", "magistrateSignature", "description", "courtAppearance.date", "courtAppearance.time", "courtAppearance.location","boundDownOrder.meaning", "boundDownOrder.dateOfOrder", "boundDownOrder.duration",
  "boundDownOrder.bondAmount", "boundDownOrder.amountPerSurety", "boundDownOrder.totalSureties",
  "boundDownOrder.suretyConditions", "boundDownOrder.consequenceOnDefault",
  "boundDownOrder.summary", "boundDownOrder.effectiveFrom", "boundDownOrder.effectiveTo"];

    requiredFields.forEach((field) => {
      const error = validateField(field, getNestedValue(formData, field));
      if (error) {
        setNestedError(errors, field, error);
        hasErrors = true;
      }
    });

    // Validate respondents
    if (formData.respondent) {
      formData.respondent.forEach((respondent, index) => {
        ["name", "father_name", "age", "address"].forEach((field) => {
          const error = validateField(`respondent.${field}`, getNestedValue(respondent, field));
          if (error) {
            if (!Array.isArray(errors.respondent)) errors.respondent = [];
            if (!errors.respondent[index]) errors.respondent[index] = {};
            (errors.respondent[index] as any)[field] = error;
            hasErrors = true;
          }
        });
      });
    }

    setValidationErrors(errors);
    return !hasErrors;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showValidationToast(validationErrors);
      return;
    }

    const postParams = JSON.parse(JSON.stringify(formData));
    const policeStation:any = getClosestPoliceStation(postParams.policeStation);
    postParams.reportDate = postParams.reportDate ? new Date(postParams.reportDate).toISOString() : "";
    postParams.noticeIssueDate = postParams.noticeIssueDate ? new Date(postParams.noticeIssueDate).toISOString() : null;
    if (postParams.courtAppearance) postParams.courtAppearance.date = postParams.courtAppearance.date ? new Date(postParams.courtAppearance.date).toISOString() : "";
    if (postParams.boundDownOrder) {
      postParams.boundDownOrder.dateOfOrder = postParams.boundDownOrder.dateOfOrder ? new Date(postParams.boundDownOrder.dateOfOrder).toISOString() : "";
      postParams.boundDownOrder.effectiveFrom = postParams.boundDownOrder.effectiveFrom ? new Date(postParams.boundDownOrder.effectiveFrom).toISOString() : "";
      postParams.boundDownOrder.effectiveTo = postParams.boundDownOrder.effectiveTo ? new Date(postParams.boundDownOrder.effectiveTo).toISOString() : "";
    }
    postParams.policeStation = policeStation.name;
    postParams.document = {
      connect: { id: postParams.parentDocumentId || "" },
    };
    delete postParams.file_name;
    delete postParams.parentDocumentId;
    delete postParams.documentType;
    const userInfo = getUserInfo();
    if (userInfo) {
      postParams.createdBy = {
        connect: { id: userInfo.id },
      };
      if (postParams.boundDownOrder)
        postParams.boundDownOrder.createdBy = {
          connect: { id: userInfo.id },
        };
    }

    const payload = {
      ...postParams,
      boundDownOrder: { create: postParams.boundDownOrder },
    };

    try {
      const response = await apiService.post('bnsl129-notice', JSON.stringify(payload));
      if (response) {
        toast.success("BNSL 129 Notice saved successfully!", {
          description: "The notice has been saved.",
          action: { label: "Ok", onClick: () => console.log("Ok") },
        });
        onOpenChange(false);
      } else {
        showError("Failed to save notice. Please check the form and try again.");
      }
    } catch (error: any) {
      showError(error);
    }
  };

  const ErrorMessage = ({ error }: { error: string }) =>
    error ? (
      <div className="flex items-center gap-1 text-red-600 text-sm mt-1">
        <AlertCircle size={14} />
        <span>{error}</span>
      </div>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[95vh] bg-white flex flex-col w-[95%]" style={{ maxWidth: "1200px" }}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold text-black">BNSL 129 Notice</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-4">
          {/* Basic Case Info */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Case Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Case Number</Label>
                <Input disabled={isPreviewMode} value={formData.caseNumber || ""} onChange={(e) => handleInputChange("caseNumber", e.target.value)} onBlur={(e) => handleFieldBlur("caseNumber", e.target.value)} className={getFieldError("caseNumber") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("caseNumber")} />
              </div>
              <div>
                <Label>Section</Label>
                <Input disabled={isPreviewMode} value={formData.section || ""} onChange={(e) => handleInputChange("section", e.target.value)} onBlur={(e) => handleFieldBlur("section", e.target.value)} className={getFieldError("section") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("section")} />
              </div>
              <div>
                <Label>Police Station</Label>
                <Input disabled={isPreviewMode} value={formData.policeStation || ""} onChange={(e) => handleInputChange("policeStation", e.target.value)} onBlur={(e) => handleFieldBlur("policeStation", e.target.value)} className={getFieldError("policeStation") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("policeStation")} />
              </div>
              <div>
                <Label>District</Label>
                <Input disabled={isPreviewMode} value={formData.district || ""} onChange={(e) => handleInputChange("district", e.target.value)} onBlur={(e) => handleFieldBlur("district", e.target.value)} className={getFieldError("district") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("district")} />
              </div>
              <div>
                <Label>Court</Label>
                <Input disabled={isPreviewMode} value={formData.court || ""} onChange={(e) => handleInputChange("court", e.target.value)} onBlur={(e) => handleFieldBlur("court", e.target.value)} className={getFieldError("court") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("court")} />
              </div>
              <div>
                <Label>Petitioner</Label>
                <Input disabled={isPreviewMode} value={formData.petitioner || ""} onChange={(e) => handleInputChange("petitioner", e.target.value)} onBlur={(e) => handleFieldBlur("petitioner", e.target.value)} className={getFieldError("petitioner") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("petitioner")} />
              </div>
            </CardContent>
          </Card>

          {/* Respondents */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Respondents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {formData.respondent?.map((resp: any, index: number) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3 border p-3 rounded-lg">
                  <div>
                    <Label>Name</Label>
                    <Input disabled={isPreviewMode} value={resp.name || ""} onChange={(e) => handleNestedArrayChange("respondent", index, "name", e.target.value)} onBlur={(e) => handleFieldBlur(`respondent.${index}.name`, e.target.value)} className={getFieldError(`respondent.${index}.name`) ? "border-red-500" : ""} />
                    <ErrorMessage error={getFieldError(`respondent.${index}.name`)} />
                  </div>
                  <div>
                    <Label>Father's Name</Label>
                    <Input disabled={isPreviewMode} value={resp.father_name || ""} onChange={(e) => handleNestedArrayChange("respondent", index, "father_name", e.target.value)} onBlur={(e) => handleFieldBlur(`respondent.${index}.father_name`, e.target.value)} className={getFieldError(`respondent.${index}.father_name`) ? "border-red-500" : ""} />
                    <ErrorMessage error={getFieldError(`respondent.${index}.father_name`)} />
                  </div>
                  <div>
                    <Label>Age</Label>
                    <Input disabled={isPreviewMode} value={resp.age || ""} onChange={(e) => handleNestedArrayChange("respondent", index, "age", e.target.value)} onBlur={(e) => handleFieldBlur(`respondent.${index}.age`, e.target.value)} className={getFieldError(`respondent.${index}.age`) ? "border-red-500" : ""} />
                    <ErrorMessage error={getFieldError(`respondent.${index}.age`)} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Address</Label>
                    <Textarea disabled={isPreviewMode} value={resp.address || ""} onChange={(e) => handleNestedArrayChange("respondent", index, "address", e.target.value)} onBlur={(e) => handleFieldBlur(`respondent.${index}.address`, e.target.value)} className={getFieldError(`respondent.${index}.address`) ? "border-red-500" : ""} />
                    <ErrorMessage error={getFieldError(`respondent.${index}.address`)} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Notice Details */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Notice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Notice Subject</Label>
                <Textarea disabled={isPreviewMode} rows={2} value={formData.noticeSubject || ""} onChange={(e) => handleInputChange("noticeSubject", e.target.value)} onBlur={(e) => handleFieldBlur("noticeSubject", e.target.value)} className={getFieldError("noticeSubject") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("noticeSubject")} />
              </div>
              <div>
                <Label>Legal Action</Label>
                <Textarea disabled={isPreviewMode} rows={2} value={formData.legalAction || ""} onChange={(e) => handleInputChange("legalAction", e.target.value)} onBlur={(e) => handleFieldBlur("legalAction", e.target.value)} className={getFieldError("legalAction") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("legalAction")} />
              </div>
              <div>
                <Label>Magistrate Signature</Label>
                <Input disabled={isPreviewMode} value={formData.magistrateSignature || ""} onChange={(e) => handleInputChange("magistrateSignature", e.target.value)} onBlur={(e) => handleFieldBlur("magistrateSignature", e.target.value)} className={getFieldError("magistrateSignature") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("magistrateSignature")} />
              </div>
            </CardContent>
          </Card>

          {/* Court Appearance */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Court Appearance</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Date</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.courtAppearance?.date || ""} onChange={(e) => handleNestedChange("courtAppearance", "date", e.target.value)} onBlur={(e) => handleFieldBlur("courtAppearance.date", e.target.value)} className={getFieldError("courtAppearance.date") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("courtAppearance.date")} />
              </div>
              <div>
                <Label>Time</Label>
                <Input disabled={isPreviewMode} type="time" value={formData.courtAppearance?.time || ""} onChange={(e) => handleNestedChange("courtAppearance", "time", e.target.value)} onBlur={(e) => handleFieldBlur("courtAppearance.time", e.target.value)} className={getFieldError("courtAppearance.time") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("courtAppearance.time")} />
              </div>
              <div>
                <Label>Location</Label>
                <Input disabled={isPreviewMode} value={formData.courtAppearance?.location || ""} onChange={(e) => handleNestedChange("courtAppearance", "location", e.target.value)} onBlur={(e) => handleFieldBlur("courtAppearance.location", e.target.value)} className={getFieldError("courtAppearance.location") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("courtAppearance.location")} />
              </div>
            </CardContent>
          </Card>

          {/* Bound Down Order */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Bound Down Order</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Meaning</Label>
                <Input disabled={isPreviewMode} value={formData.boundDownOrder?.meaning || ""} onChange={(e) => handleBoundDownOrderChange("meaning", e.target.value)} onBlur={(e) => handleFieldBlur("boundDownOrder.meaning", e.target.value)} className={getFieldError("boundDownOrder.meaning") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.meaning")} />
              </div>
              <div>
                <Label>Date of Order</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.boundDownOrder?.dateOfOrder || ""} onChange={(e) => handleBoundDownOrderChange("dateOfOrder", e.target.value)} onBlur={(e) => handleFieldBlur("boundDownOrder.dateOfOrder", e.target.value)} className={getFieldError("boundDownOrder.dateOfOrder") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.dateOfOrder")} />
              </div>
              <div>
                <Label>Duration</Label>
                <Input disabled={isPreviewMode} value={formData.boundDownOrder?.duration || ""} onChange={(e) => handleBoundDownOrderChange("duration", e.target.value)} onBlur={(e) => handleFieldBlur("boundDownOrder.duration", e.target.value)} className={getFieldError("boundDownOrder.duration") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.duration")} />
              </div>
              <div>
                <Label>Bond Amount</Label>
                <Input disabled={isPreviewMode} type="number" value={formData.boundDownOrder?.bondAmount || ""} onChange={(e) => handleBoundDownOrderChange("bondAmount", Number(e.target.value))} onBlur={(e) => handleFieldBlur("boundDownOrder.bondAmount", e.target.value)} className={getFieldError("boundDownOrder.bondAmount") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.bondAmount")} />
              </div>
              <div>
                <Label>Amount Per Surety</Label>
                <Input disabled={isPreviewMode} type="number" value={formData.boundDownOrder?.amountPerSurety || ""} onChange={(e) => handleBoundDownOrderChange("amountPerSurety", Number(e.target.value))} onBlur={(e) => handleFieldBlur("boundDownOrder.amountPerSurety", e.target.value)} className={getFieldError("boundDownOrder.amountPerSurety") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.amountPerSurety")} />
              </div>
              <div>
                <Label>Total Sureties</Label>
                <Input disabled={isPreviewMode} type="number" value={formData.boundDownOrder?.totalSureties || ""} onChange={(e) => handleBoundDownOrderChange("totalSureties", Number(e.target.value))} onBlur={(e) => handleFieldBlur("boundDownOrder.totalSureties", e.target.value)} className={getFieldError("boundDownOrder.totalSureties") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.totalSureties")} />
              </div>
              <div className="md:col-span-2">
                <Label>Surety Conditions</Label>
                <Textarea disabled={isPreviewMode} value={formData.boundDownOrder?.suretyConditions || ""} onChange={(e) => handleBoundDownOrderChange("suretyConditions", e.target.value)} onBlur={(e) => handleFieldBlur("boundDownOrder.suretyConditions", e.target.value)} className={getFieldError("boundDownOrder.suretyConditions") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.suretyConditions")} />
              </div>
              <div className="md:col-span-2">
                <Label>Consequence on Default</Label>
                <Textarea disabled={isPreviewMode} value={formData.boundDownOrder?.consequenceOnDefault || ""} onChange={(e) => handleBoundDownOrderChange("consequenceOnDefault", e.target.value)} onBlur={(e) => handleFieldBlur("boundDownOrder.consequenceOnDefault", e.target.value)} className={getFieldError("boundDownOrder.consequenceOnDefault") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.consequenceOnDefault")} />
              </div>
              <div className="md:col-span-2">
                <Label>Summary</Label>
                <Textarea disabled={isPreviewMode} value={formData.boundDownOrder?.summary || ""} onChange={(e) => handleBoundDownOrderChange("summary", e.target.value)} onBlur={(e) => handleFieldBlur("boundDownOrder.summary", e.target.value)} className={getFieldError("boundDownOrder.summary") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.summary")} />
              </div>
              <div>
                <Label>Effective From</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.boundDownOrder?.effectiveFrom || ""} onChange={(e) => handleBoundDownOrderChange("effectiveFrom", e.target.value)} onBlur={(e) => handleFieldBlur("boundDownOrder.effectiveFrom", e.target.value)} className={getFieldError("boundDownOrder.effectiveFrom") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.effectiveFrom")} />
              </div>
              <div>
                <Label>Effective To</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.boundDownOrder?.effectiveTo || ""} onChange={(e) => handleBoundDownOrderChange("effectiveTo", e.target.value)} onBlur={(e) => handleFieldBlur("boundDownOrder.effectiveTo", e.target.value, { effectiveFrom: formData.boundDownOrder?.effectiveFrom })} className={getFieldError("boundDownOrder.effectiveTo") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("boundDownOrder.effectiveTo")} />
              </div>
            </CardContent>
          </Card>

          {/* Dates & Description */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Dates & Description</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Report Sent Date / रिपोर्ट प्रेषण दिनांक</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.reportDate || ""} onChange={(e) => handleInputChange("reportDate", e.target.value)} onBlur={(e) => handleFieldBlur("reportDate", e.target.value)} className={getFieldError("reportDate") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("reportDate")} />
              </div>
              <div>
                <Label>Court Notice Date / न्यायालय नोटिस दिनांक (if received)</Label>
                <Input disabled={isPreviewMode} type="date" value={formData.noticeIssueDate || ""} onChange={(e) => handleInputChange("noticeIssueDate", e.target.value)} onBlur={(e) => handleFieldBlur("noticeIssueDate", e.target.value)} className={getFieldError("noticeIssueDate") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("noticeIssueDate")} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea disabled={isPreviewMode} rows={3} value={formData.description || ""} onChange={(e) => handleInputChange("description", e.target.value)} onBlur={(e) => handleFieldBlur("description", e.target.value)} className={getFieldError("description") ? "border-red-500" : ""} />
                <ErrorMessage error={getFieldError("description")} />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex-shrink-0 flex justify-end space-x-2 pt-4 border-t border-gray-200 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-300">
            Cancel
          </Button>

          {isPreviewMode ? (
            <>
              <Button variant="outline" onClick={() => setIsPreviewMode(false)}>
                Edit Again
              </Button>
              <Button onClick={handleSubmit} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                Confirm & Submit
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                if (validateForm()) {
                  setTimeout(() => {
                    const scrollable = document.querySelector(".flex-1.overflow-y-auto.pr-4") as HTMLElement;
                    if (scrollable) {
                      scrollable.scrollTo({ top: 0, behavior: "smooth" });
                    } else {
                      console.warn("Scroll viewport not found");
                    }
                  }, 200);

                  setIsPreviewMode(true);
                } else {
                  showValidationToast(validationErrors);
                }
              }}
              className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
            >
              Preview Before Submit
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
